import React, { useState, useEffect } from 'react';
import { format, parseISO, eachHourOfInterval, set, isWithinInterval, addDays, subDays, addMinutes, isToday, startOfDay, endOfDay, isBefore, isAfter, parse } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { cn } from "@/lib/utils";
import { ServiceRequest } from './types';
import ServiceDetailDialog from './components/ServiceDetailDialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

interface TimeSlotViewProps {
  date: Date;
  requests: ServiceRequest[];
  compact?: boolean;
  readOnly?: boolean;
  onSendReminder?: (requestId: string) => Promise<{ success: boolean; error?: string; }>;
  onReschedule?: (requestId: string, newDate: Date) => Promise<void>;
  onDateChange?: (newDate: Date) => void;
  onTimeSlotSelect?: (time: string) => void;
  availableTimeSlots?: string[];
  conflictingSlots?: {[timeSlot: string]: string[]};
  highlightSelectedTime?: string;
  serviceDuration?: number;
  serviceDurationUnit?: string;
  onFirstAvailableTimeSlot?: (timeSlot: string) => void;
}

const TimeSlotView = ({ 
  date, 
  requests, 
  compact = false,
  readOnly = false,
  onSendReminder, 
  onReschedule,
  onDateChange,
  onTimeSlotSelect,
  availableTimeSlots,
  conflictingSlots,
  highlightSelectedTime,
  serviceDuration,
  serviceDurationUnit,
  onFirstAvailableTimeSlot
}: TimeSlotViewProps) => {
  const { i18n } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedServicePreview, setSelectedServicePreview] = useState<{
    startTime: string;
    height: number;
    top: number;
  } | null>(null);
  const [firstAvailableFound, setFirstAvailableFound] = useState(false);

  // Get the appropriate locale for date-fns based on current language
  const getDateFnsLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return enUS;
    }
  };

  // Format date with proper French capitalization rules
  const formatDateWithLocale = (date: Date, formatString: string) => {
    const formatted = format(date, formatString, { locale: getDateFnsLocale() });
    
    // In French, days and months should be lowercase unless at start of sentence
    if (i18n.language === 'fr') {
      return formatted.toLowerCase();
    }
    
    return formatted;
  };

  // Generate all time slots based on service duration or default to half-hourly
  const generateTimeSlots = () => {
    const slots: Date[] = [];
    const startHour = 6; // 6 AM
    const endHour = 22; // 10 PM
    const defaultInterval = 30; // 30 minutes default (changed from 60)
    
    // Determine the interval based on service duration
    const interval = (serviceDuration && serviceDuration < 60 && serviceDurationUnit === 'minutes') 
      ? serviceDuration 
      : defaultInterval;
    
    // Create slots from start hour to end hour with appropriate intervals
    const dayStart = set(date, { hours: startHour, minutes: 0, seconds: 0 });
    const dayEnd = set(date, { hours: endHour, minutes: 0, seconds: 0 });
    
    let currentSlot = new Date(dayStart);
    while (isBefore(currentSlot, dayEnd)) {
      slots.push(new Date(currentSlot));
      currentSlot = addMinutes(currentSlot, interval);
    }
    
    return slots;
  };

  // Generate time slots based on service duration
  const timeSlots = generateTimeSlots();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // every minute
    
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (highlightSelectedTime && serviceDuration) {
      const [hours, minutes] = highlightSelectedTime.split(':').map(Number);
      const startTime = set(date, { hours, minutes });
      
      const durationInMinutes = serviceDurationUnit === 'hours' 
        ? serviceDuration * 60 
        : serviceDuration;
      
      const dayStart = set(date, { hours: 6, minutes: 0, seconds: 0 });
      const minutesFromStart = 
        (startTime.getHours() - dayStart.getHours()) * 60 + 
        startTime.getMinutes() - dayStart.getMinutes();
      
      setSelectedServicePreview({
        startTime: highlightSelectedTime,
        top: minutesFromStart,
        height: durationInMinutes < 30 ? 30 : durationInMinutes // Ensure preview is at least 30px height
      });
    } else {
      setSelectedServicePreview(null);
    }
  }, [highlightSelectedTime, serviceDuration, serviceDurationUnit, date]);

  // Find first available time slot on component mount and when date or requests change
  useEffect(() => {
    if (!onTimeSlotSelect || !onFirstAvailableTimeSlot || firstAvailableFound) return;
    
    let firstAvailableTime: string | null = null;
    
    // Only find available slots for today and future dates
    if (!isBefore(date, startOfDay(new Date()))) {
      for (const hour of timeSlots) {
        // For today, skip time slots in the past
        if (isToday(date) && isBefore(hour, new Date())) continue;
        
        const timeStr = format(hour, 'HH:mm');
        
        if (isTimeSlotAvailable(hour) && !isTimeSlotOccupied(hour)) {
          firstAvailableTime = timeStr;
          break;
        }
      }
    }
    
    if (firstAvailableTime) {
      onFirstAvailableTimeSlot(firstAvailableTime);
      setFirstAvailableFound(true);
    }
  }, [date, requests, timeSlots, onTimeSlotSelect, onFirstAvailableTimeSlot, firstAvailableFound]);

  const getDayRequests = () => {
    console.log(`Getting requests for day: ${format(date, 'yyyy-MM-dd')}`);
    console.log(`Total requests: ${requests.length}`);
    
    const filteredRequests = readOnly 
      ? requests.filter(r => r.status === 'accepted' || r.status === 'completed')
      : requests;
    
    return filteredRequests.filter(request => {
      if (!request.scheduled_at) return false;
      
      try {
        const requestTime = parseISO(request.scheduled_at);
        const dayStartTime = startOfDay(date);
        const dayEndTime = endOfDay(date);
        
        const isWithinDay = isWithinInterval(requestTime, { start: dayStartTime, end: dayEndTime });
        
        if (isWithinDay) {
          console.log(`Request found for date ${format(date, 'yyyy-MM-dd')}: ${request.id} at ${format(requestTime, 'HH:mm')}`);
        }
        
        return isWithinDay;
      } catch (error) {
        console.error('Error parsing date:', error, request);
        return false;
      }
    });
  };

  const dayRequests = getDayRequests();

  const calculateRequestDisplay = (request: ServiceRequest) => {
    if (!request.scheduled_at) return { top: 0, height: 60 };
    
    try {
      const requestTime = parseISO(request.scheduled_at);
      const dayStart = set(date, { hours: 6, minutes: 0, seconds: 0 });
      
      const duration = request.services?.duration || 60;
      const durationInMinutes = request.services?.duration_unit === 'hours' 
        ? duration * 60 
        : duration;
      
      // Calculate minutes from 6 AM, including both hours and minutes
      const minutesFromStart = 
        (requestTime.getHours() - dayStart.getHours()) * 60 + 
        requestTime.getMinutes();
      
      const top = minutesFromStart;
      
      // Ensure service has minimum visible height
      const height = durationInMinutes < 30 ? 30 : durationInMinutes;
      
      return { top, height };
    } catch (error) {
      console.error('Error calculating position:', error, request);
      return { top: 0, height: 60 };
    }
  };

  const findOverlappingRequests = (request: ServiceRequest, allRequests: ServiceRequest[]) => {
    if (!request.scheduled_at) return [];
    
    const confirmedRequests = allRequests.filter(r => 
      (r.status === 'accepted' || r.status === 'completed') && 
      r.id !== request.id
    );
    
    const requestStart = parseISO(request.scheduled_at);
    const requestDuration = request.services?.duration || 60;
    const requestDurationMinutes = request.services?.duration_unit === 'hours' 
      ? requestDuration * 60 
      : requestDuration;
    const requestEnd = addMinutes(requestStart, requestDurationMinutes);
    
    return confirmedRequests.filter(otherRequest => {
      if (!otherRequest.scheduled_at) return false;
      
      const otherStart = parseISO(otherRequest.scheduled_at);
      const otherDuration = otherRequest.services?.duration || 60;
      const otherDurationMinutes = otherRequest.services?.duration_unit === 'hours' 
        ? otherDuration * 60 
        : otherDuration;
      const otherEnd = addMinutes(otherStart, otherDurationMinutes);
      
      return (
        (otherStart < requestEnd && otherStart >= requestStart) ||
        (otherEnd > requestStart && otherEnd <= requestEnd) ||
        (otherStart <= requestStart && otherEnd >= requestEnd)
      );
    });
  };

  const organizeRequestsWithOverlaps = () => {
    const sortedRequests = [...dayRequests].sort((a, b) => {
      if (!a.scheduled_at || !b.scheduled_at) return 0;
      return parseISO(a.scheduled_at).getTime() - parseISO(b.scheduled_at).getTime();
    });
    
    const requestsWithPosition: Array<ServiceRequest & { position: number, total: number }> = [];
    
    for (const request of sortedRequests) {
      const overlapping = findOverlappingRequests(request, sortedRequests);
      const overlappingGroup = [request, ...overlapping].sort((a, b) => {
        if (!a.scheduled_at || !b.scheduled_at) return 0;
        return parseISO(a.scheduled_at).getTime() - parseISO(b.scheduled_at).getTime();
      });
      
      const position = overlappingGroup.findIndex(r => r.id === request.id);
      
      requestsWithPosition.push({
        ...request,
        position,
        total: overlappingGroup.length
      });
    }
    
    return requestsWithPosition;
  };

  const getRequestsForHour = (hour: Date) => {
    return dayRequests.filter(request => {
      if (!request.scheduled_at) return false;
      
      try {
        const requestTime = parseISO(request.scheduled_at);
        const hourEnd = set(hour, { minutes: 59, seconds: 59 });
        return isWithinInterval(requestTime, { start: hour, end: hourEnd });
      } catch (error) {
        console.error('Error parsing date in TimeSlotView:', error, request);
        return false;
      }
    });
  };

  const getAppointmentHeight = (request: ServiceRequest) => {
    if (!request.services?.duration) return 'auto';
    
    const durationInMinutes = request.services.duration_unit === 'hours' 
      ? request.services.duration * 60 
      : request.services.duration;
    
    if (compact) {
      return `${Math.max(40, Math.min(120, durationInMinutes * 0.67))}px`;
    } else {
      return `${Math.max(60, Math.min(180, durationInMinutes))}px`;
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: "bg-yellow-500",
      accepted: "bg-green-500",
      rejected: "bg-red-500",
      completed: "bg-blue-500",
      cancelled: "bg-gray-500"
    };
    return colors[status as keyof typeof colors] || "bg-gray-500";
  };

  const handleRequestClick = (request: ServiceRequest, event: React.MouseEvent) => {
    if (readOnly) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    setSelectedRequest(request);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedRequest(null);
  };

  const handlePreviousDay = () => {
    if (onDateChange) {
      onDateChange(subDays(date, 1));
    }
  };

  const handleNextDay = () => {
    if (onDateChange) {
      onDateChange(addDays(date, 1));
    }
  };

  const formatDuration = (request: ServiceRequest) => {
    if (!request.services?.duration) return '';
    
    const duration = request.services.duration;
    const unit = request.services.duration_unit || 'minutes';
    
    if (unit === 'hours') {
      return duration === 1 ? '1 hr' : `${duration} hrs`;
    } else {
      return duration === 1 ? '1 min' : `${duration} min`;
    }
  };

  const calculateCurrentTimePosition = () => {
    if (!isToday(date)) return -1;
    
    const dayStart = set(date, { hours: 6, minutes: 0, seconds: 0 });
    const minutesFromStart = 
      (currentTime.getHours() - dayStart.getHours()) * 60 + 
      currentTime.getMinutes();
    
    return minutesFromStart;
  };

  const handleTimeSlotClick = (hour: Date) => {
    if (!onTimeSlotSelect) return;
    
    const timeStr = format(hour, 'HH:mm');
    if (isTimeSlotAvailable(hour) && !isTimeSlotOccupied(hour) && !isTimeSlotInPast(hour)) {
      onTimeSlotSelect(timeStr);
    }
  };

  const isTimeSlotAvailable = (hour: Date): boolean => {
    if (!availableTimeSlots) return true;
    const timeStr = format(hour, 'HH:mm');
    return availableTimeSlots.includes(timeStr);
  };

  const getTimeSlotConflicts = (hour: Date): string[] | undefined => {
    if (!conflictingSlots) return undefined;
    const timeStr = format(hour, 'HH:mm');
    return conflictingSlots[timeStr];
  };
  
  const isTimeSlotOccupied = (hour: Date): boolean => {
    if (!requests || requests.length === 0) return false;
    
    const hourStart = hour;
    // For 30-minute intervals, the end should be 29 minutes later to avoid overlap with the next slot
    const slotDuration = 30;
    const hourEnd = addMinutes(hour, slotDuration - 1);
    
    return requests.some(request => {
      if (!request.scheduled_at) return false;
      
      const requestTime = parseISO(request.scheduled_at);
      const requestDuration = request.services?.duration || 60;
      const requestDurationMinutes = request.services?.duration_unit === 'hours' 
        ? requestDuration * 60 
        : requestDuration;
      const requestEnd = addMinutes(requestTime, requestDurationMinutes);
      
      return (
        (requestTime >= hourStart && requestTime <= hourEnd) ||
        (requestEnd > hourStart && requestEnd <= hourEnd) ||
        (requestTime < hourStart && requestEnd > hourEnd)
      );
    });
  };

  const isTimeSlotInPast = (hour: Date): boolean => {
    if (!isToday(date)) return isBefore(date, new Date());
    const now = new Date();
    return isBefore(hour, now);
  };

  const currentTimePosition = calculateCurrentTimePosition();
  const organizedRequests = organizeRequestsWithOverlaps();

  return (
    <div className={cn(
      "border rounded-lg shadow bg-white overflow-hidden",
      compact ? "text-xs" : ""
    )}>
      <div className={cn(
        "border-b flex items-center justify-between",
        compact ? "p-2" : "p-4"
      )}>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handlePreviousDay}
          disabled={!onDateChange}
          className={cn("h-8 w-8", !onDateChange && "hidden")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        <h3 className={cn(
          "font-semibold text-gebeya-purple", 
          compact ? "text-sm" : "text-lg"
        )}>
          {!compact && (
            <>
              {formatDateWithLocale(date, 'EEEE, d MMMM')}
            </>
          )}
          {compact && (
            <>
              {formatDateWithLocale(date, 'EEE, d MMM')}
            </>
          )}
        </h3>
        
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={handleNextDay}
          disabled={!onDateChange}
          className={cn("h-8 w-8", !onDateChange && "hidden")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="divide-y max-w-full">
        <div className="relative" style={{ minHeight: compact ? '960px' : '960px' }}>
          {timeSlots.map((hour) => {
            const timeStr = format(hour, 'HH:mm');
            const isAvailable = isTimeSlotAvailable(hour);
            const conflicts = getTimeSlotConflicts(hour);
            const isHighlighted = highlightSelectedTime === timeStr;
            const isOccupied = isTimeSlotOccupied(hour);
            const isPast = isTimeSlotInPast(hour);
            const isSelectable = isAvailable && !isOccupied && !isPast && onTimeSlotSelect !== undefined;
            
            return (
              <div
                key={hour.toISOString()}
                className={cn(
                  "border-b border-gray-200 relative",
                  isSelectable && "hover:bg-blue-50 transition-colors cursor-pointer",
                  !isAvailable && !isOccupied && "bg-gray-100",
                  isHighlighted && "bg-blue-100 border border-blue-300",
                  ((!isAvailable || isOccupied || isPast) && onTimeSlotSelect !== undefined) && "cursor-not-allowed",
                  hour.getMinutes() === 30 && "border-b-dashed"
                )}
                style={{ 
                  height: '30px',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(hour.getHours() - 6) * 60 + hour.getMinutes()}px`,
                }}
                onClick={() => isSelectable && handleTimeSlotClick(hour)}
              >
                <div className={cn(
                  "absolute left-2 top-0 text-xs",
                  isSelectable ? "text-gray-700" : "text-gray-400"
                )}>
                  {format(hour, 'h:mm a', { locale: getDateFnsLocale() })}
                </div>
                
                {isOccupied && (
                  <div 
                    className="absolute inset-x-0 bg-pink-200 h-full z-0" 
                    style={{ top: 0, opacity: 0.7 }}
                  />
                )}
                
                {isPast && (
                  <div className="absolute inset-x-0 bg-gray-200 h-full z-0" 
                    style={{ top: 0, opacity: 0.5 }}
                  />
                )}
                
                {!isAvailable && !isOccupied && (
                  <div className="absolute inset-0 bg-gray-100 opacity-70" />
                )}
                
                {conflicts && conflicts.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="absolute right-2 top-0 z-10">
                          <div className="flex items-center justify-center h-full">
                            <div className="w-4 h-4 bg-red-500 rounded-full" />
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Conflicts with: {conflicts.join(', ')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                
                {isHighlighted && (
                  <div className="absolute inset-0 border-2 border-blue-400 rounded-sm z-5 pointer-events-none"></div>
                )}
              </div>
            );
          })}

          {currentTimePosition >= 0 && (
            <div 
              className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
              style={{ top: `${currentTimePosition}px` }}
            >
              <div className="absolute -top-2 -left-1 h-4 w-4 rounded-full bg-red-500"></div>
            </div>
          )}

          {organizedRequests.map((request) => {
            const { top, height } = calculateRequestDisplay(request);
            const width = request.total > 1 ? `${100 / request.total}%` : '98%';
            const left = request.total > 1 ? `${(request.position * 100) / request.total}%` : '1%';
            
            const overlappingRequests = findOverlappingRequests(request, dayRequests);
            const hasOverlap = overlappingRequests.length > 0;
            
            const isConflictingConfirmedBooking = 
              (request.status === 'accepted' || request.status === 'completed') && 
              hasOverlap;
            
            return (
              <div
                key={request.id}
                className={cn(
                  "absolute rounded-md px-1.5 py-0.5 text-white overflow-y-auto transition-opacity hover:opacity-80",
                  getStatusColor(request.status),
                  compact ? "text-[8px]" : "text-xs",
                  readOnly ? "" : "cursor-pointer"
                )}
                style={{ 
                  top: `${top}px`, 
                  height: `${height}px`, 
                  width,
                  left,
                  maxHeight: `${height}px`,
                  border: isConflictingConfirmedBooking ? '2px solid red' : 'none',
                  zIndex: 5,
                  pointerEvents: readOnly ? 'none' : 'auto'
                }}
                onClick={(e) => handleRequestClick(request, e)}
              >
                <div className={cn(
                  "font-medium leading-tight whitespace-normal flex items-start justify-between",
                  compact ? "text-[8px]" : "text-[11px]"
                )}>
                  <span>{request.services.name}</span>
                  {isConflictingConfirmedBooking && !compact && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex-shrink-0 ml-1">!</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Conflicting appointment! This booking overlaps with {overlappingRequests.length} other booking(s).</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!compact && request.services?.duration && (
                    <span className="ml-1 opacity-80">({formatDuration(request)})</span>
                  )}
                </div>
                {!compact && (
                  <>
                    <div className="text-[10px] leading-tight opacity-90 whitespace-normal">{request.customer_name}</div>
                    {request.scheduled_at && (
                      <div className="text-[10px] leading-tight">{format(parseISO(request.scheduled_at), 'h:mm a', { locale: getDateFnsLocale() })}</div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {selectedServicePreview && (
            <div 
              className="absolute rounded-md px-1.5 py-0.5 bg-blue-400 text-white overflow-y-auto opacity-70 z-3 pointer-events-none"
              style={{
                top: `${selectedServicePreview.top}px`,
                height: `${selectedServicePreview.height}px`,
                width: '98%',
                left: '1%',
              }}
            >
              <div className="font-medium text-[11px] leading-tight">
                {serviceDuration && (
                  <span className="flex items-center justify-center h-full">
                    Selected: {format(parse(selectedServicePreview.startTime, 'HH:mm', new Date()), 'h:mm a', { locale: getDateFnsLocale() })} 
                    {' - '}
                    {format(
                      addMinutes(
                        parse(selectedServicePreview.startTime, 'HH:mm', new Date()),
                        serviceDurationUnit === 'hours' ? serviceDuration * 60 : serviceDuration
                      ),
                      'h:mm a',
                      { locale: getDateFnsLocale() }
                    )}
                    {' ('}
                    {serviceDuration} {serviceDurationUnit === 'hours' ? 
                      (serviceDuration === 1 ? 'hour' : 'hours') : 
                      (serviceDuration === 1 ? 'minute' : 'minutes')}
                    {')'}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!readOnly && (
        <ServiceDetailDialog 
          isOpen={isDialogOpen} 
          onClose={handleCloseDialog} 
          request={selectedRequest}
          onSendReminder={onSendReminder}
          onReschedule={onReschedule}
        />
      )}
    </div>
  );
};

export default TimeSlotView;
